import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'en' | 'ja';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  toggleLanguage: () => void;
  isTransitioning: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // initialize language from localstorage or default to english
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('lumina-language');
    return (saved === 'en' || saved === 'ja') ? saved : 'en';
  });

  const [isTransitioning, setIsTransitioning] = useState(false);

  const handleSetLanguage = (newLanguage: Language) => {
    setLanguage(newLanguage);
    localStorage.setItem('lumina-language', newLanguage);
  };

  const toggleLanguage = () => {
    setIsTransitioning(true);

    setTimeout(() => {
      const newLanguage = language === 'en' ? 'ja' : 'en';
      handleSetLanguage(newLanguage);

      setTimeout(() => {
        setIsTransitioning(false);
      }, 100);
    }, 150);
  };

  const value: LanguageContextType = {
    language,
    setLanguage: handleSetLanguage,
    toggleLanguage,
    isTransitioning,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;